import { useQueryData } from "@src/hooks/useQueryData";
import { byCode, patchCachedListItem } from "@src/hooks/queryListCache";
import type { WsDepotCardExtends } from "../../../../../api/wsDepotCards";
import { wsInvActsServices, type WsInvActContent, type WsInvActContentExtends } from "../../../../../api/wsInvActs";

/** Связи строки состава АИ для списка карточки. */
export const WS_INV_ACT_CONTENT_LIST_INCLUDES = {
    include: ["WsDepotCard"] satisfies Array<keyof WsInvActContentExtends>,
    includeWsDepotCard: ["Stockobj", "Measure"] satisfies Array<keyof WsDepotCardExtends>,
} as const;

export interface UseWsInvActContentsQueryResult {
    content: WsInvActContent[];
    isFetching: boolean;
    refetch: () => Promise<unknown>;
    /**
     * Патчит `FactQty` строки в списке и в detail-query формы без полной перезагрузки состава.
     * @param contentCode код строки состава АИ
     * @param factQty актуальное фактическое количество после мутации
     */
    patchContentFactQty: (contentCode: number, factQty: number) => void;
}

/**
 * Server-state состава АИ на TanStack Query (без pagination/load-more).
 */
export function useWsInvActContentsQuery(actCode: number): UseWsInvActContentsQueryResult {
    const query = useQueryData({
        queryKey: ["workshop", "wsInvAct", actCode, "contents"],
        queryFn: async () => {
            const { items } = await wsInvActsServices.content.load({
                flt: { ActInvCodes: [actCode] },
                ...WS_INV_ACT_CONTENT_LIST_INCLUDES,
            });
            return items;
        },
        enabled: actCode > 0,
        meta: { errorSource: "workshop.wsInvAct.contents" },
    });

    const patchContentFactQty = (contentCode: number, factQty: number): void => {
        patchCachedListItem(query.setData, contentCode, { FactQty: factQty }, byCode);
        query.setDataForKey<WsInvActContent>(["workshop", "wsInvAct", "content", contentCode], (prevData) =>
            prevData == null ? prevData : { ...prevData, FactQty: factQty },
        );
    };

    return {
        content: query.data ?? [],
        isFetching: query.isFetching,
        refetch: query.refetch,
        patchContentFactQty,
    };
}
